/**
 * HTTP Client
 * Professional fetch wrapper with:
 * - Automatic auth headers (Bearer token or X-Guest-Id)
 * - ETag support for optimistic concurrency control
 * - Workspace ID headers
 * - Error handling with typed errors
 * - Retry logic for transient failures
 */

import { env } from './env'
import { etagKey, getEtag, rememberEtag } from './etag'
import { refreshAccessToken } from './apiClient'
import { getAuthItem, removeAuthItem, AUTH_STORAGE_KEYS } from './authStorage'
import { getActiveWorkspaceId } from './api/workspace-context'
/**
 * HTTP Error with status code
 */
export class HttpError extends Error {
  readonly status: number
  readonly payload?: unknown
  /**
   * The parsed error response body, when the server returned JSON.
   *
   * `payload` is the *string* message extracted from the body (kept for
   * existing consumers); `body` is the same parsed JSON object the message was
   * extracted from — callers that need a structured field (e.g. the
   * `connected: false` demotion branch) read it from here instead of having to
   * re-parse `payload`. `undefined` for non-JSON / unparseable responses.
   */
  readonly body?: unknown

  constructor(message: string, status: number, payload?: unknown, body?: unknown) {
    super(message)
    this.name = 'HttpError'
    this.status = status
    this.payload = payload
    this.body = body
  }
}

/**
 * Container warming up error (503 with X-Container-Status: warming-up)
 */
export class ContainerWarmingUpError extends Error {
  readonly retryAfter: number

  constructor(message: string, retryAfter: number = 3) {
    super(message)
    this.name = 'ContainerWarmingUpError'
    this.retryAfter = retryAfter
  }
}

/**
 * Request options
 */
export interface HttpRequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  headers?: Record<string, string>
  body?: unknown
  authToken?: string | null
  guestId?: string | null
  /** Cache key to fetch ETag from (for If-Match header) */
  ifMatchFrom?: string | null
  /** Direct ETag value for If-Match header (takes precedence over ifMatchFrom) */
  ifMatch?: string | null
  signal?: AbortSignal
  /** Search params to append to URL */
  searchParams?: Record<string, string>
  /** Internal flag to prevent infinite retry loops on 401 */
  _skipRetry?: boolean
}

/**
 * Response wrapper with data and ETag
 */
export interface HttpResponse<T> {
  data: T
  etag: string | null
  status: number
}

/**
 * Create full URL with search params
 */
function createUrl(path: string, searchParams?: Record<string, string>): string {
  // Handle absolute URLs
  const url = path.startsWith('http') ? new URL(path) : new URL(path, env.apiBaseUrl)

  // Append search params
  if (searchParams) {
    for (const [key, value] of Object.entries(searchParams)) {
      url.searchParams.set(key, value)
    }
  }

  return url.toString()
}

/**
 * Extract an error message — and, when the body is JSON, the parsed object —
 * from a non-2xx response.
 *
 * The response can only be consumed once, so this is the single place that
 * reads the failed body: it returns BOTH the human-readable `message` (used for
 * the thrown Error's text + `HttpError.payload`) and the parsed `body` (exposed
 * as `HttpError.body` for callers that need a structured field). `body` is
 * `undefined` for non-JSON / unparseable responses.
 */
async function extractError(response: Response): Promise<{ message: string; body?: unknown }> {
  try {
    const contentType = response.headers.get('Content-Type') ?? ''
    if (contentType.includes('application/json')) {
      const json = await response.json()
      if (typeof json?.message === 'string') return { message: json.message, body: json }
      if (typeof json?.error === 'string') return { message: json.error, body: json }
      return { message: JSON.stringify(json), body: json }
    }
    return { message: await response.text() }
  } catch {
    return { message: response.statusText }
  }
}

/**
 * Core HTTP request function
 */
export async function request<T = unknown>(
  path: string,
  options: HttpRequestOptions = {},
): Promise<HttpResponse<T>> {
  const {
    method = 'GET',
    headers = {},
    body,
    authToken: explicitAuthToken,
    guestId,
    ifMatchFrom,
    ifMatch,
    signal,
    searchParams,
    _skipRetry,
  } = options

  // Fall back to the stored token when no explicit one was passed. Matches
  // apiGet/apiPost in lib/api/client.ts so callers don't have to thread the
  // token through every callsite. Pass `authToken: null` to force an
  // unauthenticated request.
  const authToken = explicitAuthToken === undefined && typeof window !== 'undefined'
    ? getAuthItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN)
    : explicitAuthToken

  const url = createUrl(path, searchParams)

  // Build headers - use selected container's owner ID as workspace
  const requestHeaders: Record<string, string> = {
    'Accept': 'application/json',
    'x-workspace-id': getActiveWorkspaceId(),
    ...headers,
  }

  // Add Content-Type for requests with body
  if (body) {
    requestHeaders['Content-Type'] = 'application/json'
  }

  // Add auth header and extract user ID from token
  if (authToken) {
    requestHeaders['Authorization'] = `Bearer ${authToken}`

    // Extract user ID from JWT token payload (between first and second dot)
    let userIdFromToken: string | null = null
    try {
      const payload = authToken.split('.')[1]
      if (payload) {
        const decoded = JSON.parse(atob(payload))
        if (decoded.userId || decoded.sub || decoded.user_id) {
          userIdFromToken = decoded.userId || decoded.sub || decoded.user_id
        }
      }
    } catch (e) {
      // If token decode fails (e.g., session token, not JWT), fall back to sessionStorage
    }

    // Use extracted user ID from token, or fall back to auth storage
    const storedUserId = typeof window !== 'undefined' ? getAuthItem('userId') : null
    const userId = userIdFromToken || storedUserId
    if (userId) {
      requestHeaders['x-user-id'] = userId
    }

    // Add X-User-Email header for supervisor routing (magic link sessions)
    const storedEmail = typeof window !== 'undefined' ? getAuthItem('userEmail') : null
    if (storedEmail) {
      requestHeaders['X-User-Email'] = storedEmail
    }
  } else if (guestId) {
    requestHeaders['X-Guest-Id'] = guestId
    requestHeaders['x-user-id'] = guestId
  }

  // Add If-Match header for optimistic concurrency
  // Direct ifMatch value takes precedence over cache lookup
  if (ifMatch) {
    requestHeaders['If-Match'] = ifMatch
  } else if (ifMatchFrom) {
    const cachedEtag = getEtag(ifMatchFrom)
    if (cachedEtag) {
      requestHeaders['If-Match'] = cachedEtag
    } else {
      console.warn('[HTTP] No cached ETag found for key:', ifMatchFrom)
    }
  }

  // Make request
  const response = await fetch(url, {
    method,
    headers: requestHeaders,
    body: body ? JSON.stringify(body) : undefined,
    signal,
    cache: 'no-store', // Prevent browser caching (React Query handles this)
    credentials: 'include', // Send cookies with cross-origin requests
  })

  // Extract ETag from response
  const resEtag = response.headers.get('ETag')

  // Remember ETag for GET/PATCH/PUT requests (for future mutations)
  // GET: initial fetch
  // PATCH/PUT: mutation returns updated resource with new ETag
  if ((method === 'GET' || method === 'PATCH' || method === 'PUT') && resEtag) {
    // Store under GET key so it can be used for next mutation
    const key = etagKey('GET', url)
    rememberEtag(key, resEtag)
  }

  // Handle errors
  if (!response.ok) {
    // Special case: container warming up
    if (response.status === 503 && response.headers.get('X-Container-Status') === 'warming-up') {
      const retryAfter = parseInt(response.headers.get('Retry-After') || '3', 10)
      const { message } = await extractError(response)
      throw new ContainerWarmingUpError(message || 'Container is warming up...', retryAfter)
    }

    // Handle 401 Unauthorized - try to refresh token and retry once.
    // EXCEPTION: integration-level 401s (e.g. a disconnected Gmail account)
    // carry { connected: false } in the body — they are NOT workspace-auth
    // failures, so a workspace-token refresh cannot fix them and the
    // refresh-failure path below must never clear the user's session for
    // them. Probe the body (on a clone, so the regular error path can still
    // read it) and fall through to the regular HttpError, whose `body`
    // drives the caller's disconnected-UI demotion handling.
    const integrationAuth401 =
      response.status === 401 &&
      ((await response
        .clone()
        .json()
        .catch(() => undefined)) as { connected?: boolean } | undefined)
        ?.connected === false
    if (response.status === 401 && authToken && !_skipRetry && !integrationAuth401) {
      console.log('[HTTP] Got 401, attempting token refresh and retry...')
      const newToken = await refreshAccessToken()

      if (newToken) {
        console.log('[HTTP] Retrying request with new token')
        // Retry the request with the new token (_skipRetry prevents infinite loop)
        return request<T>(path, {
          ...options,
          authToken: newToken,
          _skipRetry: true,
        })
      } else {
        console.log('[HTTP] Token refresh failed, clearing session')
        // Clear session and let user re-login
        if (typeof window !== 'undefined') {
          removeAuthItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN)
          removeAuthItem('userEmail')
        }
      }
    }

    // Regular error
    const { message: errorMessage, body: errorBody } = await extractError(response)
    throw new HttpError(
      `HTTP ${response.status}: ${errorMessage}`,
      response.status,
      errorMessage,
      errorBody,
    )
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return {
      data: undefined as T,
      etag: resEtag,
      status: response.status,
    }
  }

  // Parse JSON response
  let data: T
  try {
    data = await response.json()
  } catch (error) {
    throw new HttpError(
      `Failed to parse JSON response: ${error}`,
      response.status,
      await response.text(),
    )
  }

  return {
    data,
    etag: resEtag,
    status: response.status,
  }
}

/**
 * Convenience methods for common HTTP verbs
 */
export const http = {
  get: <T = unknown>(path: string, options?: Omit<HttpRequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }),

  post: <T = unknown>(path: string, body: unknown, options?: Omit<HttpRequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body }),

  patch: <T = unknown>(path: string, body: unknown, options?: Omit<HttpRequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PATCH', body }),

  put: <T = unknown>(path: string, body: unknown, options?: Omit<HttpRequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PUT', body }),

  delete: <T = unknown>(path: string, options?: Omit<HttpRequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'DELETE' }),
}
